/**
 * Empty State Components
 * Beautiful, helpful empty states following Apple Human Interface Guidelines
 */

import React from 'react';
import { FileText, Sparkles, AlertCircle, Search, Table } from 'lucide-react';

interface EmptyStateProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

/**
 * Base Empty State component
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon,
  action,
  className = ''
}) => {
  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`}>
      {/* Icon */}
      {icon && (
        <div className="mb-4 opacity-40">
          {icon}
        </div>
      )}

      {/* Title */}
      <h3 className="text-lg font-medium text-foreground-secondary mb-2">
        {title}
      </h3>

      {/* Description */}
      <p className="text-foreground-muted max-w-md mb-6">
        {description}
      </p>

      {/* Action Button */}
      {action && (
        <button
          onClick={action.onClick}
          className="min-h-[44px] px-6 py-2.5 bg-coral-500/20 hover:bg-coral-500/30
                     border border-coral-500/40 rounded-lg text-foreground-secondary
                     transition-colors font-medium"
        >
          {action.label}
        </button>
      )}
    </div>
  );
};

/**
 * Empty state for no documents uploaded
 */
export const NoDocumentsEmptyState: React.FC<{ onUpload?: () => void }> = ({ onUpload }) => {
  return (
    <EmptyState
      icon={<FileText className="w-16 h-16 text-coral-400" />}
      title="No documents yet"
      description="Upload your first invoice to start extracting data automatically with our AI-powered system."
      action={onUpload ? {
        label: 'Upload Document',
        onClick: onUpload
      } : undefined}
    />
  );
};

/**
 * Empty state for no entities found
 */
export const NoEntitiesEmptyState: React.FC<{ onRefresh?: () => void }> = ({ onRefresh }) => {
  return (
    <EmptyState
      icon={<Sparkles className="w-12 h-12 text-coral-400" />}
      title="No entities extracted"
      description="This document doesn't contain any recognizable invoice fields. Try re-analyzing or check if the document is readable."
      action={onRefresh ? {
        label: 'Re-analyze Document',
        onClick: onRefresh
      } : undefined}
    />
  );
};

/**
 * Empty state for no tables found
 */
export const NoTablesEmptyState: React.FC = () => {
  return (
    <EmptyState
      icon={<Table className="w-12 h-12 text-coral-400" />}
      title="No tables detected"
      description="This document doesn't contain any structured tables. Line items and tables will appear here when detected."
    />
  );
};

/**
 * Empty state for search with no results
 */
export const NoSearchResultsEmptyState: React.FC<{ searchTerm: string; onClear?: () => void }> = ({
  searchTerm,
  onClear
}) => {
  return (
    <EmptyState
      icon={<Search className="w-12 h-12 text-coral-400" />}
      title="No results found"
      description={`No documents match "${searchTerm}". Try different keywords or clear your search.`}
      action={onClear ? {
        label: 'Clear Search',
        onClick: onClear
      } : undefined}
    />
  );
};

/**
 * Empty state for filtered results
 */
export const NoFilterResultsEmptyState: React.FC<{ onClearFilters?: () => void }> = ({ onClearFilters }) => {
  return (
    <EmptyState
      icon={<FileText className="w-12 h-12 text-coral-400" />}
      title="No documents match your filters"
      description="Try adjusting your filters to see more results, or clear all filters to start fresh."
      action={onClearFilters ? {
        label: 'Clear All Filters',
        onClick: onClearFilters
      } : undefined}
    />
  );
};

/**
 * Empty state for processing/loading
 */
export const ProcessingEmptyState: React.FC<{ message?: string }> = ({
  message = 'Processing your document...'
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      {/* Animated loading spinner */}
      <div className="w-12 h-12 border-4 border-coral-500/30 border-t-coral-500 rounded-full animate-spin mb-4" />

      {/* Message */}
      <p className="text-foreground-muted">
        {message}
      </p>
    </div>
  );
};

/**
 * Empty state for errors
 */
export const ErrorEmptyState: React.FC<{
  title?: string;
  message: string;
  onRetry?: () => void;
}> = ({
  title = 'Something went wrong',
  message,
  onRetry
}) => {
  return (
    <EmptyState
      icon={<AlertCircle className="w-12 h-12 text-red-400" />}
      title={title}
      description={message}
      action={onRetry ? {
        label: 'Try Again',
        onClick: onRetry
      } : undefined}
    />
  );
};

/**
 * Empty state for learning stats (no corrections yet)
 */
export const NoCorrectionsEmptyState: React.FC = () => {
  return (
    <EmptyState
      icon={<Sparkles className="w-12 h-12 text-coral-400" />}
      title="No corrections yet"
      description="The system learns from your corrections. Start correcting entities to help improve accuracy for future documents."
    />
  );
};

export default EmptyState;
